// src/components/StarRating/StarRating.jsx
import PropTypes from "prop-types";
import { useState, useCallback, useMemo, useRef } from "react";
import { FaStar, FaRegStar } from "react-icons/fa";

/**
 * StarRating
 * ----------------------------------------------------------
 * Kevyt, riippuvuudet: vain react-icons.
 * Parannukset:
 *  - precision: 0.5 OLETUS (voit laittaa 0.1 → kymmenesosat)
 *  - Hover-ennakointi (hiiri näyttää arvon ennen klikkausta)
 *  - Klikkaus missä kohtaa tahansa tähteä → täsmäarvo (quantized by precision)
 *  - Näppäimistö: ←/→/↑/↓ = ±precision, Home=0, End=5, Enter/Space = commit
 *  - readOnly-tila (näyttää arvon, ei interaktiota)
 *  - A11y: rooli "slider", aria-* attribuutit, kun interaktiivinen
 *  - Focus-ring Tailwindilla
 *
 * Mistä säädät:
 *  - värit: props colors.filled / colors.empty / colors.hover / colors.stroke
 *  - koko: size (px)
 *  - tarkkuus: precision (0.5 tai 0.1)
 *  - lukutila: readOnly
 *  - näytä numero ja (count): showValue, showCount
 */

export default function StarRating({
  rating = 0,                 // 0..5 (controlled)
  onRatingChange,             // (next:number) => void
  readOnly = false,           // true = vain näyttö
  disabled = false,           // true = disabloitu (harmaana, ei fokusoi)
  precision = 0.5,            // 0.5 (puolikkaat) TAI 0.1 (kymmenesosat)
  size = 30,                  // px
  className = "",
  showValue = false,          // näytä "4.2"
  showCount = false,          // näytä "(123)"
  count = 0,
  label = "Arvio",            // ruudunlukijalle
  colors = {
    filled: "rgb(250 204 21)",          // keltainen-400
    empty: "rgba(148,163,184,0.35)",    // slate-400/35
    hover: "rgb(234 179 8)",            // keltainen-500
    stroke: "rgba(255,255,255,0.35)",
  },
}) {
  const containerRef = useRef(null);
  const [hoverValue, setHoverValue] = useState(null); // null = ei hoveria
  const active = (hoverValue ?? rating) || 0;

  // Varmista rajat
  const clampedActive = useMemo(
    () => Math.max(0, Math.min(5, Number.isFinite(active) ? active : 0)),
    [active]
  );

  // Näppäimistö – toimii, jos ei readOnly/disabled ja onChange on annettu
  const step = precision;
  const canInteract = !readOnly && !disabled && typeof onRatingChange === "function";

  const commit = useCallback(
    (value) => {
      if (!canInteract) return;
      const q = quantize(value, step);
      onRatingChange(q);
    },
    [canInteract, onRatingChange, step]
  );

  const onKeyDown = useCallback(
    (e) => {
      if (!canInteract) return;
      if (e.key === "ArrowRight" || e.key === "ArrowUp") {
        e.preventDefault();
        commit(Math.min(5, clampedActive + step));
      } else if (e.key === "ArrowLeft" || e.key === "ArrowDown") {
        e.preventDefault();
        commit(Math.max(0, clampedActive - step));
      } else if (e.key === "Home") {
        e.preventDefault();
        commit(0);
      } else if (e.key === "End") {
        e.preventDefault();
        commit(5);
      } else if (e.key === " " || e.key === "Enter") {
        // Enter/Space: jos hoverValue on määritelty, commitoi se
        if (hoverValue != null) {
          e.preventDefault();
          commit(hoverValue);
        }
      }
    },
    [canInteract, clampedActive, commit, hoverValue, step]
  );

  // Laske hover- ja klikkausarvot pointerin X-sijainnista
  const calcValueFromPointer = useCallback(
    (clientX) => {
      const el = containerRef.current;
      if (!el) return null;
      const rect = el.getBoundingClientRect();
      const x = clientX - rect.left;          // 0 ... width
      const starW = Number(size);
      const totalW = starW * 5;
      const clampX = Math.max(0, Math.min(totalW, x));
      const raw = (clampX / totalW) * 5;      // 0..5
      return quantize(raw, step);
    },
    [size, step]
  );

  const onMouseMove = useCallback(
    (e) => {
      if (!canInteract) return;
      const next = calcValueFromPointer(e.clientX);
      if (next != null) setHoverValue(next);
    },
    [canInteract, calcValueFromPointer]
  );

  const onTouchMove = useCallback(
    (e) => {
      if (!canInteract) return;
      const t = e.touches?.[0];
      if (!t) return;
      const next = calcValueFromPointer(t.clientX);
      if (next != null) setHoverValue(next);
    },
    [canInteract, calcValueFromPointer]
  );

  const onClick = useCallback(
    (e) => {
      if (!canInteract) return;
      const next = calcValueFromPointer(e.clientX);
      if (next != null) commit(next);
    },
    [canInteract, calcValueFromPointer, commit]
  );

  const onLeave = () => setHoverValue(null);

  /** Yhden tähden renderöinti osittaisella täytöllä (0..1) */
  const renderStar = (index /* 1..5 */) => {
    // Kuinka suuri osuus tästä tähdestä täyttyy näytettävässä arvossa
    const fraction = starFractionFor(clampedActive, index); // 0..1
    const fillColor = hoverValue != null ? colors.hover : colors.filled;

    // Leikataan täyttö vasemmalta oikealle käyttäen CSS clipPathia
    const clip = `inset(0 ${100 - fraction * 100}% 0 0)`;

    return (
      <span
        key={index}
        className="relative inline-block"
        style={{ width: size, height: size, lineHeight: 0 }}
        aria-hidden="true"
      >
        {/* Tyhjä ääriviiva */}
        <FaRegStar size={size} color={colors.empty} />

        {/* Täyttö (täysi/puolikas/kymmenesosa) clipPathilla */}
        {fraction > 0 && (
          <FaStar
            size={size}
            color={fillColor}
            style={{
              position: "absolute",
              inset: 0,
              clipPath: clip,
              transition: "clip-path 120ms ease",
              // Kevyt hohde
              filter: "drop-shadow(0 0 8px rgba(250,204,21,0.35))",
            }}
          />
        )}

        {/* Hento scale-efekti hoverissa, vain interaktiivisessa tilassa */}
        {canInteract && (
          <span
            className="absolute inset-0 rounded"
            style={{ transition: "transform 120ms ease" }}
          />
        )}
      </span>
    );
  };

  return (
    <div className={`inline-flex items-center gap-2 ${className}`}>
      {/* Interaktiivinen tähti-alue */}
      <div
        ref={containerRef}
        className={[
          "relative select-none",
          canInteract ? "cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-yellow-400 rounded-md" : "",
          disabled ? "opacity-60 pointer-events-none" : "",
        ].join(" ")}
        style={{ width: Number(size) * 5, height: Number(size) }}
        role={canInteract ? "slider" : "img"}
        aria-label={label}
        aria-valuemin={canInteract ? 0 : undefined}
        aria-valuemax={canInteract ? 5 : undefined}
        aria-valuenow={canInteract ? Number(clampedActive.toFixed(2)) : undefined}
        aria-readonly={readOnly || undefined}
        aria-disabled={disabled || undefined}
        tabIndex={canInteract ? 0 : -1}
        onKeyDown={onKeyDown}
        onMouseMove={onMouseMove}
        onMouseLeave={onLeave}
        onTouchMove={onTouchMove}
        onClick={onClick}
      >
        <div className="flex items-center" style={{ gap: 4 }}>
          {Array.from({ length: 5 }, (_, i) => renderStar(i + 1))}
        </div>
      </div>

      {/* (valinnainen) numeronäyttö ja kappalemäärä */}
      {(showValue || showCount) && (
        <div className="text-sm text-white/80 dark:text-white/70 tabular-nums">
          {showValue && <span>{clampedActive.toFixed(precision < 0.5 ? 1 : 1)}</span>}
          {showValue && showCount && <span>&nbsp;</span>}
          {showCount && <span>({count})</span>}
        </div>
      )}
    </div>
  );
}

/* ---------- Apufunktiot ---------- */

// Leikkaa arvo precision-askeliin, esim. 3.66 → precision=0.5 → 3.5
function quantize(value, step) {
  const q = Math.round(value / step) * step;
  return Number(Math.max(0, Math.min(5, q)).toFixed(2));
}

// Paljonko yhden tähden osuus täyttyy näytettävässä arvossa (0..1)
// index on 1..5; value esim. 3.7 → tähdet: [1,1,1,0.7,0]
function starFractionFor(value, index) {
  const left = index - 1;   // tämän tähden vasen raja
  if (value <= left) return 0;
  if (value >= index) return 1;
  return value - left;      // väli (0..1)
}

StarRating.propTypes = {
  rating: PropTypes.number,
  onRatingChange: PropTypes.func, // interaktiivinen vain jos annettu
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  precision: PropTypes.number,
  size: PropTypes.number,
  className: PropTypes.string,
  showValue: PropTypes.bool,
  showCount: PropTypes.bool,
  count: PropTypes.number,
  label: PropTypes.string,
  colors: PropTypes.shape({
    filled: PropTypes.string,
    empty: PropTypes.string,
    hover: PropTypes.string,
    stroke: PropTypes.string,
  }),
};
